import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Shield, ShieldCheck } from "lucide-react";
import CaprocureLogo from "@/components/CaprocureLogo";
import { toast } from "sonner";
import PasswordChecklist from "@/components/PasswordChecklist";
import { validatePasswordClient } from "@/lib/password";

export default function RecoverAdmin() {
  const [token, setToken] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    // M-5: mirror backend policy client-side.
    const pwErr = validatePasswordClient(pw);
    if (pwErr) { setErr(pwErr); return; }
    if (pw !== confirm) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/admin-recovery/reset", { token: token.trim(), new_password: pw });
      toast.success("Admin password updated");
      setDone(true);
      setTimeout(() => nav("/login"), 1500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="max-w-lg w-full p-10 border-slate-200">
        <Link to="/" className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-md bg-[#0A2540] flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div className="font-display font-extrabold text-[#0A2540]">Caprocure</div>
        </Link>

        {done ? (
          <div className="text-center py-6" data-testid="recover-success">
            <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto">
              <ShieldCheck className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="eyebrow mt-4">All set</div>
            <h2 className="font-display text-2xl font-bold text-[#0A2540] mt-1">Password updated</h2>
            <p className="text-sm text-slate-500 mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <div className="eyebrow mb-2">Admin recovery</div>
            <h2 className="font-display text-2xl font-bold text-[#0A2540]">Set a new admin password</h2>
            <p className="text-sm text-slate-500 mt-2">Paste the one-time recovery token issued to you, then choose a new password. The token is single-use and expires in one hour.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>One-time recovery token *</Label>
                <Input
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  required
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Paste your recovery token"
                  data-testid="recover-token-input"
                />
              </div>
              <div>
                <Label>New password *</Label>
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={10} data-testid="recover-pw-input" />
                <PasswordChecklist password={pw} />
              </div>
              <div>
                <Label>Confirm new password *</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={10} data-testid="recover-confirm-input" />
              </div>
              {err && <div className="text-sm text-rose-600" data-testid="recover-error">{err}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] hover:bg-[#061A2E]" data-testid="recover-submit-btn">
                {loading ? "Updating…" : "Set new password"}
              </Button>
              <div className="text-sm text-center text-slate-600">
                Back to <Link to="/login" className="text-[#1D4ED8] font-medium">sign in</Link>
              </div>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
